/* eslint-disable @typescript-eslint/no-explicit-any */

import { AppAction } from 'src/config/authorization.config';
import { BooleanStyle } from '../types/misc/GenericTableTypes/BooleanStyle';
import { ColumnTypes } from '../types/misc/GenericTableTypes/ColumnTypes';
import { FileUploadOptions } from './FileUploadOptions';
import { Type } from '@angular/core';

/**
 * Type definition for a generic column formatter function.
 *
 * @template T - The type of the value to be formatted.
 * @param {T} value - The value to be formatted.
 * @returns {unknown} - The formatted value.
 */
type GenericColumnFormatter<T> = (value: T) => unknown;

/**
 * Configuration class for a generic table column.
 *
 * @template T - The type of the column value.
 */
export class GenericColumnConfiguration<T = any, D = any, C = any> {
  /**
   * Indicates if the column is sortable.
   * Default is true.
   * If true, the column will be sortable and the sort icon will be displayed.
   * If false, the column will not be sortable and the sort icon will not be displayed.
   */
  isSortable?: boolean;

  /**
   * Indicates if the column is filterable.
   * Default is true.
   * If true, the column will be filterable and the filter icon will be displayed.
   * If false, the column will not be filterable and the filter icon will not be displayed.
   */
  isFilterable?: boolean;

  /**
   * The type of the column.
   * Default is 'string'.
   * Possible values are: 'string' | 'longText' | 'dropdown' | 'number' | 'boolean' | 'date' | 'currency' | 'enum' | 'decimal' | 'email' | 'telephone' | 'files' | 'count' | 'user' | 'autocomplete' | 'component'.
   */
  type: ColumnTypes = 'string';

  /**
   * The field name of the objects attribute.
   */
  field!: string;

  /**
   * The default value for the column.
   */
  defaultValue?: NoInfer<T> | undefined;

  /**
   * The index of the form separate page.
   * This is used to separate fields in the create and edit forms.
   */
  formSeparatePageIndex?: number;

  /**
   * The style for boolean values in the column.
   */
  booleanStyle?: BooleanStyle;

  /**
   * Indicates if the column is a global filter field.
   */
  isGlobalFilterField?: boolean;

  /**
   * Custom export header for the column.
   * If the table is exported, this header will be used instead of the default one.
   */
  customExportHeader?: string;

  /**
   * The form control associated with the column.
   */
  formControl: any | undefined;

  /**
   * Options for dropdowns or other selection inputs.
   */
  options?: any[];

  /**
   * The primary label for options.
   */
  optionLabel?: string;

  /**
   * The secondary label for options.
   */
  optionLabel2?: string;

  // ToDo: Remove "scaffolded" and "scaffoldedField" properties. Implement a better solution for objects with nested properties.
  /**
   * The scaffolded field name.
   */
  scaffoldedField?: string;

  /**
   * Indicates if the column is scaffolded.
   */
  scaffolded?: boolean;

  /**
   * The translation path for the column.
   */
  translatePath?: string;

  /**
   * Indicates if cell actions require confirmation.
   * If true, a confirmation dialog will be displayed before the action is executed.
   */
  confirmCellAction?: boolean;

  /**
   * Indicates if the column is displayed in the table.
   */
  isTableField?: boolean;

  /**
   * Indicates if the column is included in the create form.
   */
  isCreateField?: boolean;

  /**
   * Indicates if the column is included in the edit form.
   */
  isEditField?: boolean;

  /**
   * Indicates if the column has a dropdown filter.
   */
  dropDownFilter?: boolean;

  /**
   * Indicates if the filter should be shown for the specific column.
   */
  showFilter!: boolean;

  /**
   * The width of the column.
   * If not set, the column will be auto-sized.
   */
  width?: number;

  /**
   * Indicates if the column is frozen to the left.
   */
  frozenLeft?: boolean;

  /**
   * Indicates if the column is frozen to the right.
   */
  frozenRight?: boolean;

  /**
   * Custom label for the column.
   */
  customLabel?: string;

  /**
   * The permission action(s) required for the column.
   */
  permissionAction?: AppAction | AppAction[] | undefined = undefined;

  /**
   * Indicates if the column content should be cut off with a tooltip.
   * Only works for string or longText types.
   */
  cutOffWithTooltip?: boolean | number; // number represents the max length in px

  /**
   * The minimum width of the column.
   * Only works for string or longText types.
   */
  minWidth?: number; // number represents the min width in px

  /**
   * The referring key for the column.
   * Must be an array.
   */
  referringKey?: string;

  /**
   * Options for file uploads in the column.
   */
  fileUploadOptions?: FileUploadOptions;

  /**
   * The match mode for filtering the specific cilumn
   */
  matchMode = 'contains';

  /**
   * Additional global filter fields to append.
   * use multiple keys for nested properties (e.g. ['createdBy.firstname', 'createdBy.lastname']).
   */
  appendGlobalFilterFields: string[] = [];

  /**
   * Indicates if the column should force single line display.
   */
  forceSingleLine?: boolean = false;

  /**
   * The formatter function for the column.
   */
  formatter: GenericColumnFormatter<T>;

  /**
   * Indicates if the column is clickable.
   * Use "columnClicked" in "rowActionClicked" event to handle the click event.
   * ToDo: Maybe add an indicator for which column was clicked.
   */
  isClickable?: boolean = false;

  /**
   * Indicates if the table header should be shown.
   */
  showTableHeader?: boolean = true;

  /**
   * Indicates if the boolean values are opposite.
   * Use this carefully, as it will invert the boolean values.
   */
  booleanIsOpposite?: boolean = false;

  /**
   * Optional key for the edit object, used to identify the specific property being edited
   */
  editObjectKey?: string;

  /**
   * The Angular component type to be dynamically created and inserted into the column.
   * This allows for custom components to be used within table columns.
   *
   * @type {Type<T>}
   */
  columnComponent?: Type<T>;

  /**
   * An object containing parameters to be passed to the dynamically created column component.
   * The keys should match the input properties of the column component.
   *
   * @type {Partial<C>}
   */
  columnComponentParams?: Partial<C>;

  /**
   * The Angular component type to be dynamically created and inserted into the form.
   * This allows for custom components to be used within table form.
   *
   * @type {Type<D>}
   */
  formComponent?: Type<D>;

  /**
   * An object containing parameters to be passed to the dynamically created form component.
   * The keys should match the input properties of the form component.
   *
   * @type {Partial<C>}
   */
  formComponentParams?: Partial<C>;

  /**
   * Constructs a new instance of GenericColumnConfiguration.
   *
   * @param {Partial<GenericColumnConfiguration<T>>} [config] - An optional partial configuration object to initialize the instance.
   */
  constructor(config?: Partial<GenericColumnConfiguration<T, C>>) {
    Object.assign(this, config);

    this.isSortable = this.isSortable ?? true;
    this.isFilterable = this.isFilterable ?? true;
    this.booleanStyle = this.booleanStyle ?? 'text';
    this.isGlobalFilterField = this.isGlobalFilterField ?? true;
    this.confirmCellAction = this.confirmCellAction ?? true;
    this.isTableField = this.isTableField ?? true;
    this.isCreateField = this.isCreateField ?? true;
    this.isEditField = this.isEditField ?? true;
    this.dropDownFilter = this.dropDownFilter ?? false;
    this.frozenLeft = this.frozenLeft ?? false;
    this.frozenRight = this.frozenRight ?? false;
    this.scaffolded = this.scaffolded ?? false;
    this.cutOffWithTooltip = this.cutOffWithTooltip ?? false;
    this.formatter = config?.formatter ?? ((value: T) => value);
  }
}
